import React, { useState, useEffect } from 'react';
import { adminAPI, tenantAPI, branchAPI } from '../../services/api';
import { useAuthStore, useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import usePermission from '../../hooks/usePermission';
import './Admins.css';

const Admins = () => {
    const { admin: currentAdmin } = useAuthStore();
    const { selectedBranch } = useBranchStore();
    const { canCreate, canEdit, canDelete } = usePermission('admins');
    const canManage = canCreate || canEdit || canDelete;
    const [admins, setAdmins] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);

    // Delete Confirmation State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [adminToDelete, setAdminToDelete] = useState(null);

    // Branch Assignment State
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [branchModalAdmin, setBranchModalAdmin] = useState(null);
    const [selectedBranchIds, setSelectedBranchIds] = useState([]);
    const [branchSaving, setBranchSaving] = useState(false);
    const [branchSearch, setBranchSearch] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'branch_admin',
        tenantId: '',
        branchId: '',
        status: 'active'
    });

    useEffect(() => {
        fetchAdmins();
        if (currentAdmin?.role === 'superadmin') {
            fetchTenants();
        }
        fetchBranches();
    }, [selectedBranch]);

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const params = {};
            if (selectedBranch?._id) {
                params.branchId = selectedBranch._id;
            }
            const response = await adminAPI.getAll(params);
            setAdmins(response.data.data.admins || []);
        } catch (error) {
            toast.error('Failed to fetch admins');
        } finally {
            setLoading(false);
        }
    };

    const fetchTenants = async () => {
        try {
            const response = await tenantAPI.getAll({ limit: 1000 });
            setTenants(response.data.data.tenants || []);
        } catch (error) {
            console.error('Failed to fetch tenants');
        }
    };

    const fetchBranches = async () => {
        try {
            const response = await branchAPI.getAll(null, { limit: 1000 });
            setBranches(response.data.data.branches || []);
        } catch (error) {
            console.error('Failed to fetch branches');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // If tenant changes, clear branch
        if (name === 'tenantId') {
            setFormData(prev => ({ ...prev, branchId: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData };
            if (editingAdmin && !payload.password) {
                delete payload.password; // Don't send empty password if not changing
            }

            // Remove empty strings to avoid CastErrors
            if (!payload.branchId) delete payload.branchId;
            if (!payload.tenantId) delete payload.tenantId;

            if (editingAdmin) {
                await adminAPI.update(editingAdmin._id, payload);
                toast.success('Admin updated successfully');
            } else {
                await adminAPI.create(payload);
                toast.success('Admin created successfully');
            }
            setShowModal(false);
            setEditingAdmin(null);
            resetForm();
            fetchAdmins();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleEdit = (admin) => {
        setEditingAdmin(admin);
        setFormData({
            name: admin.name || '',
            email: admin.email || '',
            password: '', // Don't show password
            role: admin.role || 'branch_admin',
            tenantId: admin.tenantId?._id || admin.tenantId || '',
            branchId: admin.branchId?._id || admin.branchId || '',
            status: admin.status || 'active'
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            role: 'branch_admin',
            tenantId: currentAdmin?.tenantId?._id || currentAdmin?.tenantId || '',
            branchId: currentAdmin?.branchId?._id || currentAdmin?.branchId || '',
            status: 'active'
        });
    };

    const confirmDelete = async () => {
        if (!adminToDelete) return;
        try {
            await adminAPI.delete(adminToDelete._id);
            toast.success('Admin deleted');
            fetchAdmins();
            setShowDeleteModal(false);
            setAdminToDelete(null);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete admin');
        }
    };

    const handleDelete = (admin) => {
        setAdminToDelete(admin);
        setShowDeleteModal(true);
    };

    const handleOpenBranchModal = (admin) => {
        setBranchModalAdmin(admin);
        const existing = (admin.allowedBranches || []).map(b => b._id || b);
        setSelectedBranchIds(existing);
        setBranchSearch('');
        setShowBranchModal(true);
    };

    const toggleBranchId = (id) => {
        setSelectedBranchIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleSaveBranches = async () => {
        if (!branchModalAdmin) return;
        setBranchSaving(true);
        try {
            await adminAPI.assignBranches(branchModalAdmin._id, selectedBranchIds);
            toast.success('Branch access updated');
            setShowBranchModal(false);
            fetchAdmins();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update branch access');
        } finally {
            setBranchSaving(false);
        }
    };

    const filteredAdmins = admins.filter(admin =>
        admin.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter branches based on selected tenant in form
    const filteredBranches = branches.filter(branch => {
        if (!formData.tenantId) return true;
        const branchTenantId = branch.tenantId?._id || branch.tenantId;
        return branchTenantId === formData.tenantId;
    });

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-user-shield"></i>
                            Administrators
                        </h1>
                        <p className="page-subtitle-modern">Manage platform, tenant, and branch admins</p>
                    </div>
                    {canCreate && (
                        <button className="btn-primary-modern" onClick={() => { resetForm(); setShowModal(true); }}>
                            <i className="fas fa-plus-circle"></i> Add Admin
                        </button>
                    )}
                </div>
            </div>

            <div className="filters-section">
                <div className="search-box-modern">
                    <i className="fas fa-search"></i>
                    <input
                        type="text"
                        placeholder="Search admins by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading admins...</p>
                </div>
            ) : filteredAdmins.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <i className="fas fa-user-shield"></i>
                    </div>
                    <h3>No Admins Found</h3>
                    <p>Try refining your search or add a new admin.</p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {filteredAdmins.map(admin => (
                        <div key={admin._id} className="resource-card-modern">
                            <div className="card-header">
                                <div className="resource-avatar" style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' }}>
                                    {admin.name ? admin.name.charAt(0).toUpperCase() : <i className="fas fa-user-shield"></i>}
                                </div>
                                <div className="resource-title">
                                    <h3>{admin.name}</h3>
                                    <span className="resource-subtitle">{admin.role.replace('_', ' ').toUpperCase()}</span>
                                </div>
                                {(canEdit || canDelete) && (
                                    <div className="card-actions">
                                        {canEdit && (
                                            <button className="action-btn-icon edit" onClick={() => handleEdit(admin)} title="Edit">
                                                <i className="fas fa-edit"></i>
                                            </button>
                                        )}
                                        {canEdit && admin.role !== 'superadmin' && (
                                            <button className="action-btn-icon share" onClick={() => handleOpenBranchModal(admin)} title="Assign Branch Access">
                                                <i className="fas fa-share-alt"></i>
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button className="action-btn-icon delete" onClick={() => handleDelete(admin)} title="Delete"
                                                disabled={admin._id === currentAdmin?.id}
                                                style={admin._id === currentAdmin?.id ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="card-body">
                                <div className="status-badges">
                                    <span className={`status-badge ${admin.status === 'active' ? 'active' : 'inactive'}`}>
                                        <span className="badge-dot"></span>
                                        {admin.status}
                                    </span>
                                </div>

                                <div className="info-grid">
                                    <div className="info-item">
                                        <i className="fas fa-envelope"></i>
                                        <div>
                                            <span className="info-label">Email</span>
                                            <span className="info-value">{admin.email}</span>
                                        </div>
                                    </div>

                                    <div className="info-item">
                                        <i className="fas fa-building"></i>
                                        <div>
                                            <span className="info-label">Scope</span>
                                            <span className="info-value">
                                                {admin.tenantId?.name || 'Platform Level'}
                                            </span>
                                        </div>
                                    </div>

                                    {admin.branchId && (
                                        <div className="info-item">
                                            <i className="fas fa-code-branch"></i>
                                            <div>
                                                <span className="info-label">Branch</span>
                                                <span className="info-value">{admin.branchId?.name}</span>
                                            </div>
                                        </div>
                                    )}
                                    {admin.allowedBranches?.length > 0 && (
                                        <div className="info-item">
                                            <i className="fas fa-share-alt"></i>
                                            <div>
                                                <span className="info-label">Shared Access</span>
                                                <span className="info-value">{admin.allowedBranches.length} branch{admin.allowedBranches.length !== 1 ? 'es' : ''}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>{editingAdmin ? 'Edit Admin' : 'Add Admin'}</h2>
                            </div>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-section">
                                    <h3 className="section-title">
                                        <i className="fas fa-user-cog"></i> Account Details
                                    </h3>
                                    <div className="form-grid">
                                        <div className="form-group-modern">
                                            <label>Role <span className="text-red-600">*</span></label>
                                            <select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                {currentAdmin?.role === 'superadmin' && (
                                                    <>
                                                        <option value="superadmin">Super Admin</option>
                                                        <option value="tenant_admin">Tenant Admin</option>
                                                    </>
                                                )}
                                                {(currentAdmin?.role === 'superadmin' || currentAdmin?.role === 'tenant_admin') && (
                                                    <option value="branch_admin">Branch Admin</option>
                                                )}
                                                <option value="support">Support</option>
                                                <option value="viewer">Viewer</option>
                                            </select>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Status</label>
                                            <select name="status" value={formData.status} onChange={handleInputChange}>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                                <option value="suspended">Suspended</option>
                                            </select>
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Name <span className="text-red-600">*</span></label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="form-group-modern">
                                            <label>Email <span className="text-red-600">*</span></label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="form-group-modern full-width">
                                            <label>{editingAdmin ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
                                            <input
                                                type="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                required={!editingAdmin}
                                                placeholder={editingAdmin ? "••••••••" : "Enter secure password"}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {(currentAdmin?.role === 'superadmin' && formData.role !== 'superadmin' || formData.role === 'branch_admin') && (
                                    <div className="form-section">
                                        <h3 className="section-title">
                                            <i className="fas fa-network-wired"></i> Assignment
                                        </h3>
                                        <div className="form-grid">
                                            {currentAdmin?.role === 'superadmin' && formData.role !== 'superadmin' && (
                                                <div className="form-group-modern">
                                                    <label>Tenant <span className="text-red-600">*</span></label>
                                                    <select
                                                        name="tenantId"
                                                        value={formData.tenantId}
                                                        onChange={handleInputChange}
                                                        required={formData.role !== 'superadmin'}
                                                    >
                                                        <option value="">Select Tenant</option>
                                                        {tenants.map(tenant => (
                                                            <option key={tenant._id} value={tenant._id}>{tenant.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            {formData.role === 'branch_admin' && (
                                                <div className="form-group-modern">
                                                    <label>Branch <span className="text-red-600">*</span></label>
                                                    <select
                                                        name="branchId"
                                                        value={formData.branchId}
                                                        onChange={handleInputChange}
                                                        required={formData.role === 'branch_admin'}
                                                    >
                                                        <option value="">Select Branch</option>
                                                        {filteredBranches.map(branch => (
                                                            <option key={branch._id} value={branch._id}>{branch.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern">
                                    <i className="fas fa-check"></i>
                                    {editingAdmin ? 'Update Admin' : 'Create Admin'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Branch Assignment Modal */}
            {showBranchModal && branchModalAdmin && (() => {
                const modalTenantId = branchModalAdmin.tenantId?._id || branchModalAdmin.tenantId;
                const availableBranches = branches.filter(b => {
                    if (!modalTenantId) return true;
                    const bTid = b.tenantId?._id || b.tenantId;
                    return bTid === modalTenantId;
                }).filter(b =>
                    !branchSearch ||
                    b.name.toLowerCase().includes(branchSearch.toLowerCase())
                );

                return (
                    <div className="modal-overlay-modern" onClick={() => setShowBranchModal(false)}>
                        <div className="modal-modern" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                            <div className="modal-header-modern">
                                <div>
                                    <h2><i className="fas fa-share-alt" style={{ marginRight: 8 }}></i>Assign Branch Access</h2>
                                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #6b7280)', marginTop: 2 }}>
                                        {branchModalAdmin.name} &mdash; {branchModalAdmin.role.replace('_', ' ')}
                                    </p>
                                </div>
                                <button className="modal-close" onClick={() => setShowBranchModal(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="modal-body-modern">
                                <div className="search-box-modern" style={{ marginBottom: '1rem' }}>
                                    <i className="fas fa-search"></i>
                                    <input
                                        type="text"
                                        placeholder="Search branches..."
                                        value={branchSearch}
                                        onChange={e => setBranchSearch(e.target.value)}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #6b7280)' }}>
                                        {selectedBranchIds.length} of {branches.filter(b => {
                                            if (!modalTenantId) return true;
                                            const bTid = b.tenantId?._id || b.tenantId;
                                            return bTid === modalTenantId;
                                        }).length} selected
                                    </span>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            type="button"
                                            style={{ fontSize: '0.8rem', background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer' }}
                                            onClick={() => setSelectedBranchIds(availableBranches.map(b => b._id))}
                                        >Select all</button>
                                        <button
                                            type="button"
                                            style={{ fontSize: '0.8rem', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}
                                            onClick={() => setSelectedBranchIds([])}
                                        >Clear</button>
                                    </div>
                                </div>
                                <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {availableBranches.length === 0 ? (
                                        <p style={{ textAlign: 'center', color: 'var(--text-secondary, #9ca3af)', padding: '2rem 0' }}>
                                            No branches found
                                        </p>
                                    ) : availableBranches.map(branch => {
                                        const checked = selectedBranchIds.includes(branch._id);
                                        return (
                                            <label
                                                key={branch._id}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                                                    padding: '0.65rem 0.9rem', borderRadius: '8px', cursor: 'pointer',
                                                    background: checked ? 'rgba(124,58,237,0.08)' : 'var(--card-bg, #f9fafb)',
                                                    border: `1px solid ${checked ? '#7c3aed' : 'var(--border-color, #e5e7eb)'}`,
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleBranchId(branch._id)}
                                                    style={{ accentColor: '#7c3aed', width: 16, height: 16, cursor: 'pointer' }}
                                                />
                                                <i className="fas fa-code-branch" style={{ color: '#7c3aed', fontSize: '0.85rem' }}></i>
                                                <span style={{ flex: 1, fontWeight: checked ? 600 : 400 }}>{branch.name}</span>
                                                {branch.tenantId?.name && (
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #9ca3af)' }}>
                                                        {branch.tenantId.name}
                                                    </span>
                                                )}
                                                {checked && <i className="fas fa-check" style={{ color: '#7c3aed', fontSize: '0.8rem' }}></i>}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowBranchModal(false)}>Cancel</button>
                                <button type="button" className="btn-primary-modern" onClick={handleSaveBranches} disabled={branchSaving}>
                                    <i className={`fas ${branchSaving ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                                    {branchSaving ? 'Saving...' : 'Save Access'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <div>
                                <h2>Delete Admin</h2>
                            </div>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="warning-box" style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#b91c1c', marginBottom: '0.5rem' }}>
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <strong>Warning</strong>
                                </div>
                                <p>Are you sure you want to delete admin <strong>{adminToDelete?.name}</strong>?</p>
                                <p style={{ fontSize: '0.85rem', color: '#991b1b', marginTop: '0.5rem' }}>
                                    This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-danger-modern" onClick={confirmDelete}>
                                <i className="fas fa-trash"></i>
                                Delete Admin
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admins;
